import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Car, Loader2, Sparkles, X, ImagePlus, ChevronDown, Check } from 'lucide-react';
import AdPlanPicker from '../components/AdPlanPicker';
import { handleAdSaved, adErrorMessage, MY_ADS_URL } from '../utils/adSubmit';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const compressImage = (file, maxDim = 1600, quality = 0.8) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }) : file),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

export default function AddVehicleListing() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [plan, setPlan] = useState(null);
  const isAr = language === 'ar';
  const fileInputRef = useRef(null);

  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [images, setImages] = useState([]); // [{file, preview, existingIndex?}]
  const [formData, setFormData] = useState({});
  const [features, setFeatures] = useState([]);
  const [openSections, setOpenSections] = useState({});
  const selectOptionsRef = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const [schemaRes, meRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/listing-schemas`),
          axios.get(`${BACKEND_URL}/api/auth/me`, { headers: getAuthHeaders() }).catch(() => null),
        ]);
        const s = schemaRes.data?.schemas?.auto;
        setSchema(s);

        const defaults = {};
        const open = {};
        const optsMap = {};
        (s?.sections || []).forEach((sec) => {
          open[sec.id] = sec.default_open !== false;
          (sec.fields || []).forEach((f) => {
            if (f.default !== undefined) defaults[f.name] = f.default;
            if (f.options) optsMap[f.name] = f.options.map((o) => String(o.value));
          });
        });
        selectOptionsRef.current = optsMap;
        const me = meRes?.data;
        if (me) {
          defaults.contactPhone = me.phone || '';
          defaults.contactWhatsapp = me.phone || '';
          defaults.contactEmail = me.email || '';
        }
        if (editId) {
          const adRes = await axios.get(`${BACKEND_URL}/api/user/ads/${editId}`, { headers: getAuthHeaders() });
          const ad = adRes.data || {};
          const d = ad.details || {};
          Object.assign(defaults, Object.fromEntries(Object.entries(d).filter(([k]) => k !== 'features')));
          defaults.price = ad.price != null ? String(ad.price) : defaults.price;
          defaults.description = ad.description || d.description || '';
          if (Array.isArray(d.features)) setFeatures(d.features);
          setImages((ad.images || []).map((src, i) => ({ file: null, preview: src, existingIndex: i })));
          Object.keys(open).forEach((k) => { open[k] = true; });
        }
        setFormData(defaults);
        setOpenSections(open);
      } catch {
        toast.error(isAr ? 'تعذر تحميل نموذج الإعلان' : 'Failed to load listing form');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAr, editId]);

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const analyzeImage = async (file) => {
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('language', language);
      const res = await axios.post(`${BACKEND_URL}/api/ai/analyze-car-image`, fd, { headers: getAuthHeaders() });
      if (res.data?.is_car === false) {
        toast.error(res.data.message || (isAr ? 'الصورة لا تحتوي على سيارة' : 'No car detected in image'));
        return;
      }
      const fields = res.data?.fields || {};
      const maxYear = new Date().getFullYear() + 1;
      setFormData((prev) => {
        const next = { ...prev };
        Object.entries(fields).forEach(([k, v]) => {
          if (k === 'is_car' || v === null || v === undefined || v === '') return;
          let val = String(v).trim();
          const opts = selectOptionsRef.current[k];
          if (opts) {
            // القيمة يجب أن تطابق أحد خيارات القائمة وإلا يظهر الحقل فارغاً
            const match = opts.find((o) => o.toLowerCase() === val.toLowerCase());
            if (!match) return;
            val = match;
          }
          if (k === 'year') {
            const y = parseInt(val, 10);
            if (!y || y < 1990 || y > maxYear) return;
            val = String(y);
          }
          next[k] = val;
        });
        return next;
      });
      toast.success(isAr ? '✨ تم التعرف على السيارة وتعبئة البيانات تلقائياً!' : '✨ Car detected — fields auto-filled!');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'تعذر تحليل الصورة' : 'Image analysis failed'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddImages = async (files) => {
    const max = schema?.images?.max || 5;
    const incoming = Array.from(files).slice(0, max - images.length);
    if (!incoming.length) return;
    const wasEmpty = images.length === 0;
    const compressed = await Promise.all(incoming.map((f) => compressImage(f)));
    const newImages = compressed.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setImages((prev) => [...prev, ...newImages]);
    if (wasEmpty) analyzeImage(compressed[0]);
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const toggleFeature = (id) => {
    setFeatures((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  };

  const handleSubmit = async () => {
    const vm = schema?.validation_messages || {};
    const lang = isAr ? 'ar' : 'en';
    if (!formData.make || !formData.model) {
      toast.error(vm.make_model?.[lang] || (isAr ? 'يرجى إدخال الماركة والموديل' : 'Enter make and model'));
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error(vm.price?.[lang] || (isAr ? 'يرجى إدخال السعر' : 'Enter price'));
      return;
    }
    if (images.length < (schema?.images?.required_min || 1)) {
      toast.error(vm.images?.[lang] || (isAr ? 'يرجى إضافة صورة واحدة على الأقل' : 'Add at least one image'));
      return;
    }
    if (!isEdit && plan && !plan.ready) {
      toast.error(isAr ? 'يرجى اختيار مدة الإعلان' : 'Please choose the ad duration');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({ ...formData, features }));
      const title = [formData.make, formData.model, formData.year].filter(Boolean).join(' ');
      if (isEdit) {
        fd.append('keep_images', JSON.stringify(images.filter((i) => i.file === null).map((i) => i.existingIndex)));
        const newFiles = images.filter((i) => i.file);
        newFiles.forEach((img, i) => fd.append(i === 0 ? 'mainImage' : `extraImage${i}`, img.file));
        const res = await axios.put(`${BACKEND_URL}/api/user/ads/vehicles/${editId}`, fd, { headers: getAuthHeaders() });
        handleAdSaved(res.data, { navigate, isAr, title });
      } else {
        if (plan && !plan.isFree) fd.append('tier_id', plan.tierId);
        fd.append('mainImage', images[0].file);
        images.slice(1).forEach((img, i) => fd.append(`extraImage${i + 1}`, img.file));
        const res = await axios.post(`${BACKEND_URL}/api/user/ads/vehicle`, fd, { headers: getAuthHeaders() });
        handleAdSaved(res.data, { navigate, isAr, title });
      }
    } catch (err) {
      toast.error(adErrorMessage(err, isAr));
    } finally {
      setSubmitting(false);
    }
  };

  const cardCls = isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white border border-[#E2E8F0]';
  const inputCls = isDarkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-[#E2E8F0]';

  const renderField = (f) => {
    const label = f[isAr ? 'label_ar' : 'label_en'];
    const placeholder = f[isAr ? 'placeholder_ar' : 'placeholder_en'] || f.placeholder || '';
    const value = formData[f.name] ?? '';

    if (f.type === 'select') {
      return (
        <div key={f.name}>
          <Label className="mb-1.5 block text-sm">{label} {f.required && '*'}</Label>
          <select
            value={value}
            onChange={(e) => setField(f.name, e.target.value)}
            className={`w-full rounded-md border px-3 py-0 h-10 leading-none text-sm ${inputCls}`}
            data-testid={`field-${f.name}`}
          >
            {!f.default && <option value="">{isAr ? '— اختر —' : '— Select —'}</option>}
            {(f.options || []).map((o) => (
              <option key={o.value} value={o.value}>{o[isAr ? 'label_ar' : 'label_en']}</option>
            ))}
          </select>
        </div>
      );
    }
    if (f.type === 'year_select') {
      const maxYear = new Date().getFullYear() + (f.max_year_offset || 0);
      const years = [];
      for (let y = maxYear; y >= (f.min_year || 1990); y--) years.push(y);
      return (
        <div key={f.name}>
          <Label className="mb-1.5 block text-sm">{label} {f.required && '*'}</Label>
          <select
            value={value}
            onChange={(e) => setField(f.name, e.target.value)}
            className={`w-full rounded-md border px-3 py-0 h-10 leading-none text-sm ${inputCls}`}
            data-testid={`field-${f.name}`}
          >
            <option value="">{isAr ? '— السنة —' : '— Year —'}</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      );
    }
    if (f.type === 'textarea') {
      return (
        <div key={f.name} className="sm:col-span-2 lg:col-span-3">
          <Label className="mb-1.5 block text-sm">{label} {f.required && '*'}</Label>
          <Textarea
            value={value}
            onChange={(e) => setField(f.name, e.target.value)}
            rows={f.rows || 3}
            placeholder={placeholder}
            data-testid={`field-${f.name}`}
          />
        </div>
      );
    }
    return (
      <div key={f.name}>
        <Label className="mb-1.5 block text-sm">{label} {f.required && '*'}</Label>
        <Input
          type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
          value={value}
          onChange={(e) => setField(f.name, e.target.value)}
          placeholder={placeholder}
          data-testid={`field-${f.name}`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-8 px-4 ${isDarkMode ? 'bg-[#0a0a0a] text-white' : 'bg-[#F8FAFC] text-[#0F172A]'}`} data-testid="add-vehicle-listing-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(MY_ADS_URL)}
          className="flex items-center gap-2 text-[#1E9BD9] mb-6 hover:underline"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowRight className={`h-4 w-4 ${!isAr ? 'rotate-180' : ''}`} />
          {isAr ? 'العودة إلى إعلاناتي' : 'Back to My Ads'}
        </button>

        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Car className="h-6 w-6 text-[#1E9BD9]" />
          {isEdit ? (isAr ? 'تعديل إعلان مركبة' : 'Edit Vehicle Ad') : (isAr ? 'إضافة إعلان مركبة' : 'Add Vehicle Ad')}
        </h1>
        <p className="text-sm text-[#94A3B8] mb-6 flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-[#1E9BD9]" />
          {isAr ? 'ارفع صورة السيارة وسيتعرف الذكاء الاصطناعي عليها ويعبي البيانات تلقائياً' : 'Upload a car photo and AI will auto-fill the details'}
        </p>

        {!isEdit && (
          <div className="mb-6">
            <AdPlanPicker category="vehicles" isAr={isAr} isDarkMode={isDarkMode} value={plan} onChange={setPlan} />
          </div>
        )}

        {/* Images */}
        <div className={`rounded-2xl p-6 mb-6 ${cardCls}`}>
          <div className="flex items-center justify-between mb-4">
            <Label className="font-bold">{isAr ? `الصور (${images.length}/${schema?.images?.max || 5})` : `Images (${images.length}/${schema?.images?.max || 5})`} *</Label>
            {analyzing && (
              <span className="flex items-center gap-2 text-sm text-[#1E9BD9]" data-testid="ai-analyzing-indicator">
                <Loader2 className="h-4 w-4 animate-spin" />
                {isAr ? 'الذكاء الاصطناعي يتعرف على السيارة...' : 'AI analyzing the car...'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-white/10">
                <img src={img.preview} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute bottom-1 start-1 text-[10px] bg-[#1E9BD9] text-white px-1.5 py-0.5 rounded">
                    {isAr ? 'رئيسية' : 'Main'}
                  </span>
                )}
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 end-1 bg-black/60 rounded-full p-1 text-white hover:bg-red-600"
                  data-testid={`remove-image-${i}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {images.length < (schema?.images?.max || 5) && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${isDarkMode ? 'border-white/20 hover:border-[#1E9BD9] text-white/50' : 'border-[#CBD5E1] hover:border-[#1E9BD9] text-[#94A3B8]'}`}
                data-testid="add-image-btn"
              >
                <ImagePlus className="h-6 w-6" />
                <span className="text-xs">{isAr ? 'إضافة صورة' : 'Add Image'}</span>
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => { handleAddImages(e.target.files); e.target.value = ''; }}
            data-testid="image-file-input"
          />
          {images.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 text-[#1E9BD9] hover:text-[#1786be]"
              onClick={() => images[0]?.file && analyzeImage(images[0].file)}
              disabled={analyzing || !images[0]?.file}
              data-testid="reanalyze-btn"
            >
              <Sparkles className="h-4 w-4 me-1.5" />
              {isAr ? 'إعادة التحليل بالذكاء الاصطناعي' : 'Re-analyze with AI'}
            </Button>
          )}
        </div>

        {/* Dynamic Sections */}
        {(schema?.sections || []).map((sec) => (
          <div key={sec.id} className={`rounded-2xl mb-6 overflow-hidden ${cardCls}`}>
            <button
              className="w-full flex items-center justify-between p-5 font-bold"
              onClick={() => setOpenSections((prev) => ({ ...prev, [sec.id]: !prev[sec.id] }))}
              data-testid={`section-toggle-${sec.id}`}
            >
              <span>{sec[isAr ? 'title_ar' : 'title_en']}</span>
              <ChevronDown className={`h-5 w-5 transition-transform ${openSections[sec.id] ? 'rotate-180' : ''}`} />
            </button>
            {openSections[sec.id] && (
              <div className="px-5 pb-5">
                {sec.note_ar && isAr && <p className="text-xs text-[#94A3B8] mb-3">{sec.note_ar}</p>}
                {sec.type === 'multi_chips' ? (
                  <div className="space-y-4">
                    {(sec.groups || []).map((g) => (
                      <div key={g.id}>
                        <p className="text-sm font-medium mb-2 text-[#94A3B8]">{g[isAr ? 'title_ar' : 'title_en']}</p>
                        <div className="flex flex-wrap gap-2">
                          {(g.items || []).map((item) => {
                            const active = features.includes(item.id);
                            return (
                              <button
                                key={item.id}
                                onClick={() => toggleFeature(item.id)}
                                className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors flex items-center gap-1.5 ${
                                  active
                                    ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]'
                                    : isDarkMode ? 'border-white/15 hover:border-white/40' : 'border-[#E2E8F0] hover:border-[#94A3B8]'
                                }`}
                                data-testid={`feature-${item.id}`}
                              >
                                {active && <Check className="h-3.5 w-3.5" />}
                                {item[isAr ? 'label_ar' : 'label_en']}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(sec.fields || []).map(renderField)}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        <Button
          onClick={handleSubmit}
          disabled={submitting || analyzing}
          className="w-full bg-[#1E9BD9] hover:bg-[#1786be] text-white h-12 text-base"
          data-testid="submit-vehicle-ad-btn"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : isEdit ? (isAr ? 'حفظ التعديلات' : 'Save Changes') : plan && !plan.isFree && plan.ready ? (isAr ? `نشر والدفع (${plan.fee} ${plan.currency})` : `Publish & Pay (${plan.fee} ${plan.currency})`) : (isAr ? 'نشر الإعلان' : 'Publish Ad')}
        </Button>
      </div>
    </div>
  );
}
